import { styled } from '@linaria/react'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import type { AppRoute } from '#app/navigation/NavigationContext'
import { useAppNavigation } from '#app/navigation/NavigationContext'
import { appRoutes } from '#app/navigation/routes/routes'
import { useAppDispatch, useAppSelector } from '#app/store'
import { closeConfiguration, setLeadingConfiguration, type TruckConfiguration } from '#app/store/slices/configurationSlice'
import { Icon } from '#components/Icon'
import { isManCodeApiConfiguration } from '#features/Configuration/ApiConfiguration'
import { lazyExportAndDownloadXlsx } from '#features/Excel'
import { hasUnsavedChanges } from '#features/ManCode/hasUnsavedChanges'
import { useShowManCodeInformationOverlay } from '#features/ManCode/useShowManCodeInformationOverlay'
import ChangeBaseParametersIcon from '#icons/calendar-48.svg'
import CloseIcon from '#icons/close-48.svg'
import CompareIcon from '#icons/compare-1-24.svg'
import CopyIcon from '#icons/copy-outline-48.svg'
import EditIcon from '#icons/edit-48.svg'
import ExportIcon from '#icons/excel-export.svg'
import FileDataIcon from '#icons/file-data-outline-24.svg'
import KeyIcon from '#icons/key-24.svg'
import PrintIcon from '#icons/print-48.svg'
import { useShowChangeBaseParametersInformationOverlay } from '#pages/Configuration/ChangeBaseParameters/useShowChangeBaseParametersInformationOverlay'
import { ConfigurationInformationOverlayElements } from '#pages/Configuration/ConfigurationInformationOverlay/ConfigurationInformationOverlay'
import {
  type AnyConfigurationLocation,
  makeComparisonConfigurationLocationRaw,
  makeConfigurationLocation,
  makeDefaultConfigurationLocation,
  makeManCodeWithSortOrder,
  makePrintConfigurationLocation,
} from '#pages/Configuration/components/ConfigurationNavigation'
import { extractConfigurationTitle } from '#pages/Configuration/components/extractConfigurationTitle'
import { style } from '#styles/index'
import { useDateFormatter } from '#utils/formatDateTime'
import { testIds } from '#utils/testIds'

const Entry = styled.div`  
  &[data-disabled='true'] {
    pointer-events: none;
    color: ${style.theme.textDisabled};
  }
  
  > span {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

export const ConfigurationDropdownContent: FC<{
  configuration: TruckConfiguration
  activeConfiguration?: TruckConfiguration
  allConfigurations: TruckConfiguration[]
  close: () => void
  startEditing: () => void
}> = ({ configuration, activeConfiguration, allConfigurations, startEditing, close }) => {
  const { navigate } = useAppNavigation()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const leadingConfiguration = useAppSelector((x) => x.truckConfigurations.leadingConfiguration)
  const unsavedChanges = hasUnsavedChanges(configuration)
  const dateFormatters = useDateFormatter()

  const isReady = configuration.step.stage !== 'PendingStep' && !configuration.step.loading && configuration.creation.loadingStep === 'done'
  const canChangeConfig = configuration.apiConfiguration.mode !== 'read-only'
  const canPerformEcmConfigurationActions = isReady && configuration.uuid
  const canPerformDetailedActions = isReady && configuration.step.requested === 'DetailedStep'
  const hasManCode = !!configuration.apiConfiguration.manCode
  const showManCode = useShowManCodeInformationOverlay()
  const showChangeBaseParameters = useShowChangeBaseParametersInformationOverlay()

  return (
    <>
      <Entry
        data-testid={testIds.header.dropdownEntry.makeLeading}
        className="_entry"
        data-disabled={configuration.id === leadingConfiguration}
        onClick={(evt) => {
          evt.preventDefault()
          evt.stopPropagation()
          dispatch(setLeadingConfiguration(configuration.id))
          navigate(makeDefaultConfigurationLocation(configuration))
          close()
        }}
      >
        <Icon iconUrl={KeyIcon} />
        <span>{t('configuration.actions.button.make-leading', 'Set as leading configuration')}</span>
      </Entry>
      <Entry
        data-testid={testIds.header.dropdownEntry.cloneConfiguration}
        className="_entry"
        data-disabled={!canPerformEcmConfigurationActions}
        onClick={(evt) => {
          evt.preventDefault()
          evt.stopPropagation()
          navigate(appRoutes['configuration/copy'].make({ configuration }))
          close()
        }}
      >
        <Icon iconUrl={CopyIcon} />
        <span>{t('configuration.actions.button.copy', 'Clone Configuration')}</span>
      </Entry>
      <ConfigurationInformationOverlayElements config={configuration} />
      {hasManCode && (
        <Entry
          data-testid={testIds.header.dropdownEntry.showManCode}
          className="_entry"
          data-disabled={!configuration.apiConfiguration.manCode}
          onClick={(evt) => {
            evt.stopPropagation()
            const configurationTitle = extractConfigurationTitle(configuration, t)
            showManCode(configuration.apiConfiguration.manCode, configurationTitle)
            close()
          }}
        >
          <Icon iconUrl={FileDataIcon} />
          <span>{t('configuration.actions.button.show-man-code', 'Show MAN-Code')}</span>
        </Entry>
      )}
      <Entry
        className="_entry"
        data-disabled={!canPerformEcmConfigurationActions || !canChangeConfig}
        onClick={(evt) => {
          evt.preventDefault()
          evt.stopPropagation()
          showChangeBaseParameters({ isShown: true, configuration })
          close()
        }}
        data-testid={testIds.header.dropdownEntry.changeBaseParameters}
      >
        <Icon iconUrl={ChangeBaseParametersIcon} />
        <span>{t('configuration.actions.button.change-base-parameters', 'Change Base Parameters')}</span>
      </Entry>
      <Entry
        className="_entry"
        data-disabled={!canPerformDetailedActions}
        onClick={(evt) => {
          evt.preventDefault()
          evt.stopPropagation()
          navigate(makePrintConfigurationLocation(configuration))
        }}
        data-testid={testIds.header.dropdownEntry.printConfiguration}
      >
        <Icon iconUrl={PrintIcon} />
        <span>{t('configuration.actions.button.print', 'Print Configuration')}</span>
      </Entry>
      <Entry
        className="_entry"
        data-disabled={!canPerformDetailedActions}
        onClick={(evt) => {
          evt.preventDefault()
          evt.stopPropagation()
          void lazyExportAndDownloadXlsx(configuration, t)
          close()
        }}
        data-testid={testIds.header.dropdownEntry.export}
      >
        <Icon iconUrl={ExportIcon} />
        <span>{t('configuration.actions.button.export', 'Export XLSX')}</span>
      </Entry>
      <Entry
        data-testid={testIds.header.dropdownEntry.compare}
        className="_entry"
        data-disabled={
          !(canPerformDetailedActions && activeConfiguration && activeConfiguration?.id !== configuration.id && allConfigurations.length > 1)
        }
        onClick={(evt) => {
          evt.preventDefault()
          evt.stopPropagation()
          close()
          if (!activeConfiguration) {
            return
          }
          const location = makeComparisonConfigurationLocationRaw(
            activeConfiguration.id,
            makeManCodeWithSortOrder(activeConfiguration),
            activeConfiguration.step.stage,
            configuration.id,
            makeManCodeWithSortOrder(configuration),
          )
          console.debug('Navigate to comparison location', location, {
            activeConfigurationId: activeConfiguration.id,
            configurationId: configuration.id,
          })
          navigate(location)
        }}
      >
        <Icon iconUrl={CompareIcon} />
        <span>{t('configuration.actions.button.compare', 'Compare')}</span>
      </Entry>
      <Entry
        data-testid={testIds.header.dropdownEntry.rename}
        className="_entry"
        data-disabled={!canChangeConfig}
        onClick={(evt) => {
          evt.preventDefault()
          evt.stopPropagation()
          startEditing()
          close()
        }}
      >
        <Icon iconUrl={EditIcon} />
        <span>{t('configuration.actions.button.rename', 'Rename')}</span>
      </Entry>
      <Entry
        data-testid={testIds.header.dropdownEntry.close}
        className="_entry"
        data-disabled="false"
        onClick={async (evt) => {
          evt.stopPropagation()
          const answer = window.confirm(
            unsavedChanges
              ? t(
                  'configuration.actions.query.close-unsaved-changes',
                  'You still have unsaved changes! Do you really want to close this configuration?',
                )
              : t('configuration.actions.query.close', 'This will close the current configuration. You have no unsaved changes.'),
          )
          if (!answer) {
            return
          }

          const index = allConfigurations.findIndex((x) => x.id === configuration.id)
          let navigationTarget: ReturnType<AppRoute['make']> | AnyConfigurationLocation | undefined
          if (activeConfiguration?.id !== configuration.id) {
            // no navigation needed if this config is not active
          } else if (index > 0) {
            navigationTarget = makeConfigurationLocation(allConfigurations[index - 1])
          } else if (allConfigurations.length > 1) {
            // index is 0, all configurations are more than one
            navigationTarget = makeConfigurationLocation(allConfigurations[index + 1])
          } else {
            navigationTarget = appRoutes.setup.make()
          }

          if (navigationTarget) {
            navigate(navigationTarget)
          }

          // TODO super annoying, if we have a MAN Code, we need to wait a bit before closing the configuration
          //      otherwise the configuration will be reopened immediately, as the current route will trigger a
          //      loading of the man code again
          if (isManCodeApiConfiguration(configuration.callingApiConfiguration) && navigationTarget) {
            await new Promise((resolve) => {
              setTimeout(() => {
                resolve(true)
              }, 250)
            })
          }

          dispatch(closeConfiguration(configuration.id))
        }}
      >
        <Icon iconUrl={CloseIcon} />
        <span>{t('shared:common.button.close')}</span>
      </Entry>
      <div className="_info">
        v{configuration.version}
        {' - '}
        {configuration.lastManCodeUpdate?.when
          ? t('configuration.actions.last-saved', {
              defaultValue: 'Saved: {{dateTime}}',
              dateTime: dateFormatters.numericDateTime(configuration.lastManCodeUpdate.when),
            })
          : t('configuration.actions.never-saved', 'Not saved yet')}
      </div>
    </>
  )
}
